import { Box, Button, Tab, Tabs, TextField } from "@mui/material";
import LoadingOverlay from "@ronchalant/react-loading-overlay";
import { useEffect, useState } from "react";
import { ReplaySubject, Subject, Subscription } from "rxjs";

import { GameState } from "../../types/entities/game-state";
import { Match } from "../../types/entities/match";
import { MatchFormat } from "../../types/entities/match-format";
import { Squad } from "../../types/entities/squad";
import { TrafficLight } from "../../types/enums/traffic-light";
import { AdminPreferences } from "../../types/preferences/admin-preferences";
import { UserPreferences } from "../../types/preferences/preferences";
import { SimulatorRoute } from "../../types/route-helpers";
import {
  services,
  showConfirmationDialog,
  showMessage,
} from "../../types/services";
import { SimulationResult } from "../../types/simulator/simulation-result";
import { PlayerFormExplanation } from "../../types/stats/form-explanation";
import { MatchStatsWrapper } from "../../types/stats/match-stats";
import {
  PlayerStats,
  PlayerStatsWrapper,
} from "../../types/stats/player-stats";
import RunSimulatorButton from "../common-components/run-simulator-button";
import { noSimulationsAllowed } from "../component-utils";
import { EntityAutoSelector } from "../entity-management/entity-selectors/entity-auto-selector";
import { TabPanel } from "../match-page/scorecard/tab-panel";
import { CreationDialog } from "../my-matches/match-creation-modals/creation-dialog";
import TooltipIconButton from "../navigation-bar/tooltip-icon-button";
import { ComparedUserSelector } from "../stats-editing-components/compared-user-selector";
import {
  areStatsEqual,
  calculateDiff,
  getToolTipMessage,
  reconcileStatsWithDiff,
} from "../stats-editing-components/stats-editing-utils";
import StealStatsButton from "../stats-editing-components/steal-stats-button";
import { FormSettingsModal } from "./form-settings-modal";
import { PlayerBattingSimulationDisplay } from "./player-batting-simulation-display";
import { PlayerBattingStatsDisplay } from "./player-batting-stats-display";
import { PlayerBowlingSimulationDisplay } from "./player-bowling-simulation-display";
import PlayerBowlingStatsDisplay from "./player-bowling-stats-display";
import PlayerFormExplanationDisplay from "./player-form-explanation-display";
import { PlayerStatsTitle } from "./player-stats-title";
import {
  batchUpdate,
  cannotGoLeft,
  cannotGoRight,
  getCurrentPlayerIndex,
  getNextPlayer,
  getOrderedSquad,
  getPreviousPlayer,
} from "./squad-page-utils";
import { StealAllPage } from "./steal-all-page";

interface State {
  currentStats: PlayerStatsWrapper;
  comparedStats: PlayerStatsWrapper;
  recentPlayerStats: Subject<PlayerStatsWrapper[]>;
  loading: boolean;
  renaming: boolean;
  nameValid: boolean;
  selectedTab: number;
  simulationLoading: boolean;
  playerStats: Map<string, PlayerStatsWrapper>;
  comparedPlayerStats: Map<string, PlayerStatsWrapper>;
  formSettingsModalOpen: boolean;
  formExplanation: PlayerFormExplanation;
  matchStats: MatchStatsWrapper;
  diffRecord: Partial<PlayerStats>;
}

interface Props {
  open: boolean;
  onCancel: () => void;
  onProceed: () => void;
  onPlayerChange: (newPlayerId: string) => void;
  teamNumber: number;
  gameState: GameState;
  matchFormat: MatchFormat;
  fromThisPoint: boolean;
  squad: Squad;
  match: Match;
  playerId: string;
  simulationResult: SimulationResult;
  comparedResult: SimulationResult;
  comparedUserName: string;
  userPreferences: UserPreferences;
  adminPreferences: AdminPreferences;
  comparedTraderConfidence: TrafficLight;
}

const defaultState: State = {
  currentStats: null,
  comparedStats: null,
  recentPlayerStats: new ReplaySubject(1),
  loading: false,
  renaming: false,
  nameValid: true,
  selectedTab: 0,
  simulationLoading: false,
  playerStats: null,
  comparedPlayerStats: new Map(),
  formSettingsModalOpen: false,
  formExplanation: null,
  matchStats: null,
  diffRecord: {},
};

export default function PlayerStatsModal({
  open,
  onCancel,
  onProceed,
  onPlayerChange,
  teamNumber,
  gameState,
  matchFormat,
  fromThisPoint,
  squad,
  match,
  playerId,
  simulationResult,
  comparedResult,
  comparedUserName,
  userPreferences,
  adminPreferences,
  comparedTraderConfidence,
}: Readonly<Props>): React.JSX.Element {
  const [state, setState] = useState<State>(defaultState);
  useEffect(() => {
    const subscriptions: Subscription[] = [
      services.simulationService.loadingSubject.subscribe(
        (simulationLoading) => {
          setState((prevState) => {
            return { ...prevState, simulationLoading };
          });
        }
      ),

      services.playerStatsService.comparedUserStatsSubject.subscribe(
        (comparedPlayerStats: Map<string, PlayerStatsWrapper>) => {
          setState((prevState) => {
            return { ...prevState, comparedPlayerStats };
          });
        }
      ),

      services.playerStatsService.playerStatsSubject.subscribe(
        (playerStats: Map<string, PlayerStatsWrapper>) => {
          setState((prevState) => {
            return { ...prevState, playerStats };
          });
        }
      ),

      services.matchStatsService.matchStatsSubject.subscribe(
        (matchStats: MatchStatsWrapper) => {
          setState((prevState) => {
            return { ...prevState, matchStats };
          });
        }
      ),
    ];

    return () =>
      subscriptions.forEach((subscription) => subscription.unsubscribe());
  }, []); // Only runs this on initial load

  useEffect(() => {
    /*
      Called whenever:
      all player stats update,
      all compared player stats update,
      the selected player changes,
      the selected compared user changes
      the game state changes

      Sets the loading overlay until the following is done:
      Get the correct player and compared stats.
      Apply any current diff that the user has applied (Will be empty in case of new player)
    */
    setState((prevState) => {
      return { ...prevState, loading: true };
    });

    if (!playerId) {
      return;
    }

    const newPlayerStats: PlayerStatsWrapper = state.playerStats?.get(playerId);
    const newComparedStats: PlayerStatsWrapper =
      state.comparedPlayerStats?.get(playerId);

    state.recentPlayerStats.next([]);
    setState((prevState) => {
      return {
        ...prevState,
        currentStats: reconcileStatsWithDiff(
          newPlayerStats,
          prevState.diffRecord
        ),
        comparedStats: newComparedStats,
        loading: false,
        renaming: false,
        nameValid: true,
        formExplanation: null,
      };
    });

    services.playerStatsService
      .getAllForPlayer(playerId, gameState.matchId)
      .then((stats: PlayerStatsWrapper[]) => {
        state.recentPlayerStats.next(stats);
      })
      .catch((reason) => {
        showMessage(`Could not load player recent stats: ${reason}`, "error");
      });
  }, [
    playerId,
    comparedUserName,
    state.comparedPlayerStats,
    state.playerStats,
    state.recentPlayerStats,
    gameState,
  ]);

  function handleNewStatsError(reason, message: string) {
    setState((prevState) => {
      return { ...prevState, loading: false };
    });
    showMessage(`${message}: ${reason}`, "error");
  }

  function regeneratePlayerStats() {
    setState((prevState) => {
      return { ...prevState, loading: true };
    });
    services.playerStatsService
      .generatePlayerStats(services.currentGameService.currentMatchId, playerId)
      .then((playerStats: PlayerStats) => {
        updateWithNewPlayerStats(playerStats);
      })
      .catch((reason) =>
        handleNewStatsError(reason, "Could not generate player stats")
      );
  }

  function getPositionalDefaults() {
    setState((prevState) => {
      return { ...prevState, loading: true };
    });
    services.playerStatsService
      .getDefaultPlayerStats(
        services.currentGameService.currentMatchId,
        playerId
      )
      .then((playerStats: PlayerStats) => {
        updateWithNewPlayerStats(playerStats);
      })
      .catch((reason) =>
        handleNewStatsError(reason, "Could not get default for position")
      );
  }

  function getPlayerDefaults() {
    setState((prevState) => {
      return { ...prevState, loading: true };
    });
    services.playerStatsService
      .getPlayerDefaultStats(
        services.currentGameService.currentMatchId,
        playerId
      )
      .then((playerStats: PlayerStats) => {
        if (playerStats) {
          updateWithNewPlayerStats(playerStats);
        } else {
          setState((prevState) => {
            return { ...prevState, loading: false };
          });
          showMessage(`No defaults saved`, "error");
        }
      })
      .catch((reason) =>
        handleNewStatsError(reason, "Could not get default for player")
      );
  }

  function updateWithNewPlayerStats(playerStats: PlayerStats) {
    const newStats = new PlayerStatsWrapper(
      state.currentStats.entityId,
      state.currentStats.playerStatsId,
      state.currentStats.createdBy,
      state.currentStats.createdAt,
      state.currentStats.name,
      playerStats,
      state.currentStats.matchType
    );

    const diffRecord = calculateDiff(
      newStats,
      state.currentStats,
      state.diffRecord,
      state.selectedTab === 0,
      state.selectedTab === 1
    );

    setState((prevState) => {
      return {
        ...prevState,
        loading: false,
        currentStats: reconcileStatsWithDiff(
          prevState.currentStats,
          diffRecord
        ),
        renaming: false,
        nameValid: true,
        formExplanation: null,
        diffRecord,
      };
    });
  }

  function explainPlayerStats() {
    setState((prevState) => {
      return { ...prevState, loading: true };
    });
    services.playerStatsService
      .explainPlayerStats(services.currentGameService.currentMatchId, playerId)
      .then((formExplanation: PlayerFormExplanation) => {
        setState((prevState) => {
          return { ...prevState, loading: false, formExplanation };
        });
      })
      .catch((reason) =>
        handleNewStatsError(reason, "Could not generate form explanation")
      );
  }

  function savePlayerDefaults() {
    services.playerStatsService.savePlayerDefaultStats(
      services.currentGameService.currentMatch.matchType,
      playerId,
      state.currentStats.playerStats
    );
  }

  function invalid() {
    return !state.nameValid;
  }

  function saveAndProceed() {
    savePlayerStats();
    onProceed();
  }

  function resetAndCancel() {
    setState((prevState) => {
      return {
        ...prevState,
        diffRecord: {},
        currentStats: state.playerStats?.get(playerId),
      };
    });
    onCancel();
  }

  function savePlayerStats() {
    setState((prevState) => {
      return { ...prevState, diffRecord: {} };
    });
    services.playerStatsService.updatePlayerStats(state.currentStats);
  }

  function changesExist(newStats: PlayerStatsWrapper) {
    return !areStatsEqual(
      state.currentStats,
      newStats,
      PlayerStatsWrapper.getAllProperties(
        StealAllPage.PLAYER_BATTING_STATS,
        StealAllPage.PLAYER_BOWLING_STATS,
        StealAllPage.BOWLING_ALGORITHM_MODAL
      ),
      "playerStats"
    );
  }

  function onStatsUpdate(newStats: PlayerStatsWrapper) {
    if (changesExist(newStats)) {
      setState((prevState) => {
        return {
          ...prevState,
          currentStats: newStats,
          diffRecord: calculateDiff(
            newStats,
            prevState.currentStats,
            prevState.diffRecord,
            true,
            true
          ),
        };
      });
    }
  }

  function onPropertyChangeValid(property: string, value: any) {
    const newStats = new PlayerStatsWrapper(
      state.currentStats.entityId,
      state.currentStats.playerStatsId,
      state.currentStats.createdBy,
      state.currentStats.createdAt,
      state.currentStats.name,
      {
        ...state.currentStats.playerStats,
        [property]: value,
      },
      state.currentStats.matchType
    );

    if (changesExist(newStats)) {
      setState((prevState) => {
        return {
          ...prevState,
          currentStats: newStats,
          diffRecord: {
            ...prevState.diffRecord,
            [property]: value,
          },
        };
      });
    }
  }

  function onArrayPropertyChangeValid(property: string, index: number, value) {
    const newValueArray = [...(state.currentStats.playerStats[property] || [])];
    newValueArray[index] = value;
    const newStats = new PlayerStatsWrapper(
      state.currentStats.entityId,
      state.currentStats.playerStatsId,
      state.currentStats.createdBy,
      state.currentStats.createdAt,
      state.currentStats.name,
      {
        ...state.currentStats.playerStats,
        [property]: newValueArray,
      },
      state.currentStats.matchType
    );

    if (changesExist(newStats)) {
      setState((prevState) => {
        return {
          ...prevState,
          currentStats: newStats,
          diffRecord: {
            ...prevState.diffRecord,
            [property]: newValueArray,
          },
        };
      });
    }
  }

  function refreshSim() {
    services.simulationService.removeSimulationResults();

    services.simulationService
      .simulateWithStats(playerId, state.currentStats)
      .catch((reason) =>
        showMessage(`Failed to run simulator: ${reason}`, "error")
      );
  }

  function updatePreferences(settings: UserPreferences) {
    services.userService.updateUserPreferences(settings).then(() =>
      setState((prevState) => {
        return { ...prevState, formSettingsModalOpen: false };
      })
    );
  }

  const onStealHandler = () => {
    batchUpdate(
      state.currentStats,
      state.comparedStats,
      PlayerStatsWrapper.getAllProperties(
        state.selectedTab === 0
          ? StealAllPage.PLAYER_BATTING_STATS
          : StealAllPage.PLAYER_BOWLING_STATS
      ),
      (updatedStats: PlayerStatsWrapper) =>
        setState((prevState) => {
          return {
            ...prevState,
            currentStats: updatedStats,
            diffRecord: calculateDiff(
              updatedStats,
              prevState.currentStats,
              prevState.diffRecord,
              state.selectedTab === 0,
              state.selectedTab === 1
            ),
          };
        })
    );
  };

  const tabName = state.selectedTab === 0 ? "Batting" : "Bowling";
  const statsButtonStyle = { fontSize: "small", width: "100%" };
  return (
    <CreationDialog
      open={open}
      classes="player-stats-modal"
      label={"Player Stats"}
      invalid={invalid()}
      disabled={false}
      loading={state.loading || state.simulationLoading}
      onCancel={() => resetAndCancel()}
      onProceed={() => saveAndProceed()}
      proceedText="OK"
      autofocus={false}
      colour="#f04343"
      fullScreen={true}
      element={
        <TooltipIconButton
          title="Form Calculation Settings"
          disabled={false}
          onClick={() =>
            setState((prevState) => {
              return { ...prevState, formSettingsModalOpen: true };
            })
          }
          icon="settings"
          colour="#ffffff"
        />
      }
    >
      <LoadingOverlay
        active={state.loading || state.simulationLoading}
        spinner
        text={state.simulationLoading ? "Simulating..." : "Loading..."}
      >
        <div className="player-stats-modal-head">
          {state.currentStats && playerId != null && (
            <PlayerStatsTitle
              onClickPrevious={() => {
                savePlayerStats();
                onPlayerChange(
                  getPreviousPlayer(gameState.squads[teamNumber - 1], playerId)
                );
              }}
              previousDisabled={
                cannotGoLeft(gameState.squads[teamNumber - 1], playerId) ||
                invalid()
              }
              onClickNext={() => {
                savePlayerStats();
                onPlayerChange(
                  getNextPlayer(gameState.squads[teamNumber - 1], playerId)
                );
              }}
              nextDisabled={
                cannotGoRight(gameState.squads[teamNumber - 1], playerId) ||
                invalid()
              }
              player={squad.players.find((p) => p.playerId.value === playerId)}
            />
          )}

          {state.currentStats && playerId && squad && (
            <div>
              <div className="stats-recalculation-buttons">
                <div className="stats-recalculation-buttons-grid">
                  <Button
                    onClick={() =>
                      showConfirmationDialog(
                        regeneratePlayerStats,
                        `Regenerate ${tabName} Form`,
                        "Are you sure?"
                      )
                    }
                    variant="contained"
                    color="secondary"
                    style={statsButtonStyle}
                  >
                    Regenerate {tabName} Form
                  </Button>
                  <Button
                    onClick={explainPlayerStats}
                    variant="contained"
                    color="secondary"
                    style={statsButtonStyle}
                  >
                    Explain Player Form
                  </Button>
                  <Button
                    onClick={() =>
                      showConfirmationDialog(
                        getPositionalDefaults,
                        `Default ${tabName} Stats for Position`,
                        "Are you sure?"
                      )
                    }
                    variant="contained"
                    color="primary"
                    style={statsButtonStyle}
                  >
                    Default {tabName} Stats for Position
                  </Button>
                  <Button
                    onClick={() =>
                      showConfirmationDialog(
                        getPlayerDefaults,
                        `Default ${tabName} Stats for Player`,
                        "Are you sure?"
                      )
                    }
                    variant="contained"
                    style={{
                      ...statsButtonStyle,
                      color: "#ffffff",
                      backgroundColor: "#3fd166",
                    }}
                  >
                    Default {tabName} Stats for Player
                  </Button>
                  <Button
                    onClick={savePlayerDefaults}
                    variant="contained"
                    color="primary"
                    style={{
                      ...statsButtonStyle,
                      color: "#ffffff",
                      backgroundColor: "#b63fd1",
                    }}
                  >
                    Update Player Defaults
                  </Button>
                  <RunSimulatorButton
                    isDisabled={
                      !playerId ||
                      !state.currentStats ||
                      !services.currentGameService.currentMatchId ||
                      noSimulationsAllowed(match, userPreferences)
                    }
                    onClick={() => refreshSim()}
                    colourOverride={{
                      trueColour: "#aaaaaa",
                      falseColour: "#222222",
                    }}
                    styleOverride={statsButtonStyle}
                  />
                </div>
              </div>

              {!state.renaming && state.recentPlayerStats && (
                <div className="stats-name">
                  <div className="stats-name-textfield">
                    <EntityAutoSelector
                      value={state.currentStats}
                      canEdit={false}
                      canClear={false}
                      options={state.recentPlayerStats}
                      optionService={services.playerStatsService}
                      renderer={(props, option) => (
                        <div {...props}>
                          {option.object.name +
                            " " +
                            new Date(option.object.createdAt)}
                        </div>
                      )}
                      classes=""
                      maxDropdownSize={5}
                      onSelect={(entity) => {
                        services.playerStatsService
                          .usePlayerStats(
                            playerId,
                            entity as PlayerStatsWrapper
                          )
                          .then(() =>
                            setState((prevState) => {
                              return {
                                ...prevState,
                                currentStats: entity as PlayerStatsWrapper,
                              };
                            })
                          )
                          .catch((reason) =>
                            showMessage(
                              `Could not change player stats: ${reason}`,
                              "error"
                            )
                          );
                      }}
                      newEntityRequiresModal={false}
                    />
                  </div>
                  <TooltipIconButton
                    title="Rename"
                    disabled={false}
                    onClick={() =>
                      setState((prevState) => {
                        return {
                          ...prevState,
                          renaming: true,
                        };
                      })
                    }
                    icon="edit"
                  />
                </div>
              )}
              {state.renaming && state.recentPlayerStats && (
                <div className="stats-name">
                  <div className="stats-name-textfield">
                    <TextField
                      value={state.currentStats.name}
                      onChange={(event) => {
                        setState((prevState) => {
                          return {
                            ...prevState,
                            nameValid: event.target.value.trimEnd().length > 0,
                            currentStats: new PlayerStatsWrapper(
                              state.currentStats.entityId,
                              state.currentStats.playerStatsId,
                              state.currentStats.createdBy,
                              state.currentStats.createdAt,
                              event.target.value,
                              state.currentStats.playerStats,
                              state.currentStats.matchType
                            ),
                          };
                        });
                      }}
                      placeholder={"Name"}
                      variant="outlined"
                      disabled={false}
                    />
                  </div>
                  <TooltipIconButton
                    title="Done renaming"
                    disabled={!state.nameValid}
                    onClick={() =>
                      setState((prevState) => {
                        return {
                          ...prevState,
                          renaming: false,
                        };
                      })
                    }
                    icon="done"
                  />
                </div>
              )}
            </div>
          )}

          {simulationResult && playerId && (
            <SimulatorRoute>
              <div className="player-stats-modal-sim-stats">
                {state.selectedTab === 0 && (
                  <PlayerBattingSimulationDisplay
                    player={
                      squad.players[
                        getCurrentPlayerIndex(
                          getOrderedSquad(gameState.squads[teamNumber - 1]),
                          playerId
                        )
                      ]
                    }
                    simulationResult={simulationResult}
                    comparedResult={comparedResult}
                    matchFormat={matchFormat}
                    gameState={gameState}
                    team={teamNumber}
                    fromThisPoint={fromThisPoint}
                    comparedTraderConfidence={comparedTraderConfidence}
                  />
                )}
                {state.selectedTab === 1 && (
                  <PlayerBowlingSimulationDisplay
                    player={
                      squad.players[
                        getCurrentPlayerIndex(
                          getOrderedSquad(gameState.squads[teamNumber - 1]),
                          playerId
                        )
                      ]
                    }
                    simulationResult={simulationResult}
                    comparedResult={comparedResult}
                    matchFormat={matchFormat}
                    gameState={gameState}
                    team={teamNumber}
                    fromThisPoint={fromThisPoint}
                    comparedTraderConfidence={comparedTraderConfidence}
                  />
                )}
              </div>
            </SimulatorRoute>
          )}

          {!simulationResult && (
            <SimulatorRoute>
              {!state.simulationLoading && (
                <div className="centered italic">No Simulation Data</div>
              )}
            </SimulatorRoute>
          )}
        </div>

        {state.currentStats && playerId && squad && (
          <div className="stats-body">
            <Box className="scorecard-top-buttons">
              <Tabs
                value={state.selectedTab}
                onChange={(_value, newValue) =>
                  setState((prevState) => {
                    return {
                      ...prevState,
                      selectedTab: newValue,
                    };
                  })
                }
                aria-label="Stats Tabs"
              >
                <Tab
                  label="Batting"
                  id="stats-tab-0"
                  aria-controls="stats-tabpanel-0"
                  style={{ padding: "0px" }}
                />
                <Tab
                  label="Bowling"
                  id="stats-tab-1"
                  aria-controls="stats-tabpanel-1"
                  style={{ padding: "0px" }}
                />
              </Tabs>

              <div className="compared-selector-and-steal-button">
                <ComparedUserSelector />
                {state.comparedStats && (
                  <StealStatsButton
                    comparedUserName={comparedUserName}
                    disabled={areStatsEqual(
                      state.currentStats,
                      state.comparedStats,
                      PlayerStatsWrapper.getAllProperties(
                        state.selectedTab === 0
                          ? StealAllPage.PLAYER_BATTING_STATS
                          : StealAllPage.PLAYER_BOWLING_STATS
                      ),
                      "playerStats"
                    )}
                    tooltipMessage={getToolTipMessage(
                      state.currentStats,
                      state.comparedStats,
                      PlayerStatsWrapper.getAllProperties(
                        state.selectedTab === 0
                          ? StealAllPage.PLAYER_BATTING_STATS
                          : StealAllPage.PLAYER_BOWLING_STATS
                      ),
                      comparedUserName,
                      "playerStats"
                    )}
                    onClickHandler={onStealHandler}
                  />
                )}
              </div>
            </Box>

            {state.formExplanation && (
              <PlayerFormExplanationDisplay
                formExplanation={state.formExplanation}
                selectedTab={state.selectedTab}
              />
            )}

            <TabPanel value={state.selectedTab} index={0}>
              <PlayerBattingStatsDisplay
                onUpdate={(currentStats) => onStatsUpdate(currentStats)}
                onValid={(property, value) =>
                  onPropertyChangeValid(property, value)
                }
                onArrayElementValid={(property, index, value) =>
                  onArrayPropertyChangeValid(property, index, value)
                }
                currentStats={state.currentStats}
                comparedStats={state.comparedStats}
                comparedUserName={comparedUserName}
                userPreferences={userPreferences}
                adminPreferences={adminPreferences}
                matchStats={state.matchStats}
                squad={squad}
                player={
                  squad.players[
                    getCurrentPlayerIndex(
                      getOrderedSquad(gameState.squads[teamNumber - 1]),
                      playerId
                    )
                  ]
                }
              />
            </TabPanel>

            <TabPanel value={state.selectedTab} index={1}>
              <PlayerBowlingStatsDisplay
                onUpdate={(currentStats) => onStatsUpdate(currentStats)}
                onArrayElementValid={(property, index, value) =>
                  onArrayPropertyChangeValid(property, index, value)
                }
                currentStats={state.currentStats}
                comparedStats={state.comparedStats}
                comparedUserName={comparedUserName}
                userPreferences={userPreferences}
                matchStats={state.matchStats}
                squad={squad}
                player={
                  squad.players[
                    getCurrentPlayerIndex(
                      getOrderedSquad(gameState.squads[teamNumber - 1]),
                      playerId
                    )
                  ]
                }
              />
            </TabPanel>
          </div>
        )}

        <FormSettingsModal
          open={state.formSettingsModalOpen}
          onCancel={() =>
            setState((prevState) => {
              return {
                ...prevState,
                formSettingsModalOpen: false,
              };
            })
          }
          onProceed={(settings) => updatePreferences(settings)}
        />
      </LoadingOverlay>
    </CreationDialog>
  );
}
